/**
 * ============================================
 * PER-CARD COUNTRY FILTER
 * ============================================
 *
 * Each tab has one floating country filter (under the date range). Every card
 * follows it by default; a card's own selector (top-right of the card) can
 * override it for that card only, and "Follow the tab filter" (or the ×)
 * returns it. Overrides live in the tab, keyed by card id.
 *
 *   <CountryScope value={...}>                 tab level: selection + overrides
 *     <CountryCard id="time">{sel => ...}</CountryCard>   sel = override ?? tab
 *       ... <CardCountryButton /> in the card header
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { CountryMultiSelect } from './CountryMultiSelect';
import { CountryChips } from './CountryChips';
import type { CountryOption } from '@/utils/countryRegions';

interface ScopeValue {
  tabSelection: string[];
  options: CountryOption[];
  isDark: boolean;
  countLabel?: string;
  disabled?: boolean;
  disabledReason?: string;
  overrides: Record<string, string[]>;
  setOverride: (id: string, codes: string[] | null) => void;
}

const ScopeContext = createContext<ScopeValue | null>(null);
const CardContext = createContext<string | null>(null);

/** Per-card overrides for one tab. Spread the result into <CountryScope>. */
export function useCardOverrides() {
  const [overrides, setOverrides] = useState<Record<string, string[]>>({});
  const setOverride = useCallback((id: string, codes: string[] | null) => {
    setOverrides(prev => {
      const next = { ...prev };
      if (codes === null) delete next[id];
      else next[id] = codes;
      return next;
    });
  }, []);
  return { overrides, setOverride };
}

export function CountryScope({ value, children }: { value: ScopeValue; children: ReactNode }) {
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

/** The effective selection of a card: its override, else the tab's. */
export function useCardSelection(id: string): string[] {
  const scope = useContext(ScopeContext);
  return scope?.overrides[id] ?? scope?.tabSelection ?? [];
}

export function CountryCard({ id, children }: { id: string; children: (selection: string[]) => ReactNode }) {
  const selection = useCardSelection(id);
  return <CardContext.Provider value={id}>{children(selection)}</CardContext.Provider>;
}

function useCardScope() {
  const scope = useContext(ScopeContext);
  const id = useContext(CardContext);
  const own = id && scope ? scope.overrides[id] : undefined;
  const onChange = useMemo(() => (codes: string[]) => scope && id && scope.setOverride(id, codes), [scope, id]);
  return scope && id ? { scope, id, own, selected: own ?? scope.tabSelection, onChange } : null;
}

/** The card's selector button alone (for headers that place chips separately). */
export function CardCountrySelect() {
  const c = useCardScope();
  if (!c) return null;
  const { scope, id, own, selected, onChange } = c;
  return (
    <CountryMultiSelect compact options={scope.options} isDark={scope.isDark} countLabel={scope.countLabel}
      selected={selected} following={own === undefined}
      onChange={onChange} onFollow={() => scope.setOverride(id, null)}
      disabled={scope.disabled} disabledReason={scope.disabledReason} />
  );
}

/** The card's removable chips alone. */
export function CardCountryChips() {
  const c = useCardScope();
  if (!c || c.scope.disabled) return null;
  return <CountryChips options={c.scope.options} selected={c.selected} onChange={c.onChange} isDark={c.scope.isDark} />;
}

/** Card header: removable chips after the title, selector at the right.
 *  Fills the space between the title and any other header controls.
 *  Renders nothing outside a CountryCard. */
export function CardCountryButton() {
  if (!useCardScope()) return null;
  return (
    <div className="flex-1 min-w-0 flex items-center justify-between gap-3 flex-wrap">
      <CardCountryChips />
      <div className="ml-auto"><CardCountrySelect /></div>
    </div>
  );
}

/** For long titles (DOMO dataset names): selector on the title row, chips on
 *  their own row below it. Goes directly in a flex-wrap header. */
export function CardCountryStacked() {
  if (!useCardScope()) return null;
  return (
    <>
      <div className="ml-auto shrink-0"><CardCountrySelect /></div>
      <div className="basis-full order-last min-w-0"><CardCountryChips /></div>
    </>
  );
}

/** A small cache of views keyed by country selection (cleared when `build` changes). */
export function useViewCache<V>(build: (selection: string[]) => V): (selection: string[]) => V {
  const cache = useMemo(() => new Map<string, V>(), [build]);
  return useCallback((selection: string[]) => {
    const key = [...selection].sort().join(',');
    let v = cache.get(key);
    if (v === undefined) {
      v = build(selection);
      cache.set(key, v);
    }
    return v;
  }, [cache, build]);
}
